(() => {
  function initJourney(root) {
    if (root.dataset.journeyBound) return;
    const link = root.querySelector('[data-gml-sell-start]');
    const panel = root.querySelector('.gml-sell__panel');
    if (!link || !panel) return;
    root.dataset.journeyBound = 'true';
    let scrollFrame = 0;
    const cancelFocus = () => cancelAnimationFrame(scrollFrame);
    const scrollToRequest = (event) => {
      event.preventDefault();
      cancelFocus();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
      if (reducedMotion) {
        panel.focus({ preventScroll: true });
        return;
      }
      let lastPosition = window.scrollY;
      let settledFrames = 0;
      const startedAt = performance.now();
      function finishScroll(now) {
        const position = window.scrollY;
        settledFrames = Math.abs(position - lastPosition) < .5 ? settledFrames + 1 : 0;
        lastPosition = position;
        if ((now - startedAt > 150 && settledFrames >= 6) || now - startedAt > 2000) {
          panel.focus({ preventScroll: true });
          scrollFrame = 0;
          return;
        }
        scrollFrame = requestAnimationFrame(finishScroll);
      }
      scrollFrame = requestAnimationFrame(finishScroll);
    };
    link.addEventListener('click', scrollToRequest);
    window.addEventListener('wheel', cancelFocus, { passive: true });
    window.addEventListener('touchstart', cancelFocus, { passive: true });
    window.addEventListener('keydown', cancelFocus);
    function unload(event) {
      if (!event.target.contains(root)) return;
      cancelFocus();
      link.removeEventListener('click', scrollToRequest);
      window.removeEventListener('wheel', cancelFocus);
      window.removeEventListener('touchstart', cancelFocus);
      window.removeEventListener('keydown', cancelFocus);
      document.removeEventListener('shopify:section:unload', unload);
    }
    document.addEventListener('shopify:section:unload', unload);
  }

  function init(root) {
    initJourney(root);
    if (root.dataset.formDesignBound) return;
    const app = root.querySelector('[data-gml-app-form]');
    const template = root.querySelector('[data-gml-form-design]');
    if (!app || !template) return;
    root.dataset.formDesignBound = 'true';
    const watched = new WeakSet();
    const enhancedDetails = new WeakMap();
    const observers = [];
    let stopped = false;
    let frame = 0;
    const fields = [
      ['name', '[id="first_name"]'],
      ['email', '[id="email"]'],
      ['phone', '[id="phone_number"]'],
      ['city', '[id="custom#city_and_country"]'],
      ['brand', '[id="custom#watch_brand-selector-label"]'],
      ['model', '[id="custom#model_and_reference_number"]'],
      ['year', '[id="custom#year_or_unknown"]'],
      ['condition', '[id="custom#condition-selector-label"]'],
      ['included', '[id="custom#box_and_papers-selector-label"]'],
      ['price', '[id="custom#expected_price_and_currency_eg_15000_cad"]'],
      ['front', '[id="custom#photo_dial_front-field"]'],
      ['back', '[id="custom#photo_case_back-field"]'],
      ['accessories', '[id="custom#photo_bracelet_clasp_or_accessories-field"]'],
      ['details', '[name="custom#additional_details_or_service_history"]']
    ];

    function enhanceDetails(control, container) {
      if (!(control instanceof HTMLTextAreaElement)) return;
      const maximum = 2000;
      control.maxLength = maximum;
      if (!control.id) control.id = 'gml-details-' + root.dataset.sectionId;
      container.querySelector('label')?.setAttribute('for', control.id);
      control.placeholder = template.dataset.detailsPlaceholder;
      control.setAttribute('aria-label', template.dataset.detailsPlaceholder);
      let meta = container.querySelector('[data-gml-details-meta]');
      if (!meta) {
        meta = document.createElement('div');
        meta.dataset.gmlDetailsMeta = '';
        const limit = document.createElement('span');
        limit.id = 'gml-details-limit-' + root.dataset.sectionId;
        limit.textContent = template.dataset.detailsLimit;
        const counter = document.createElement('span');
        counter.dataset.gmlDetailsCount = '';
        counter.setAttribute('aria-hidden', 'true');
        const status = document.createElement('span');
        status.dataset.gmlDetailsStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        meta.append(limit, counter, status);
        container.append(meta);
      }
      const descriptionIds = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      descriptionIds.add(meta.firstElementChild.id);
      control.setAttribute('aria-describedby', Array.from(descriptionIds).join(' '));
      if (enhancedDetails.has(control)) {
        enhancedDetails.get(control)();
        return;
      }
      let composing = false;
      let reached = false;
      const formatCount = new Intl.NumberFormat(document.documentElement.lang || 'en');
      const update = () => {
        if (!composing && control.value.length > maximum) {
          let value = control.value.slice(0, maximum);
          const lastCode = value.charCodeAt(value.length - 1);
          if (lastCode >= 0xD800 && lastCode <= 0xDBFF) value = value.slice(0, -1);
          control.value = value;
        }
        const currentMeta = container.querySelector('[data-gml-details-meta]');
        if (!currentMeta) return;
        const counter = currentMeta.querySelector('[data-gml-details-count]');
        const nextCount = formatCount.format(control.value.length) + '/' + formatCount.format(maximum);
        if (counter.textContent !== nextCount) counter.textContent = nextCount;
        const nextReached = control.value.length >= maximum;
        counter.dataset.limitReached = String(nextReached);
        if (nextReached !== reached) currentMeta.querySelector('[data-gml-details-status]').textContent = nextReached ? template.dataset.detailsLimitReached : '';
        reached = nextReached;
      };
      control.addEventListener('compositionstart', () => { composing = true; });
      control.addEventListener('compositionend', () => { composing = false; update(); });
      control.addEventListener('input', update);
      control.addEventListener('change', update);
      control.form?.addEventListener('reset', () => requestAnimationFrame(update));
      enhancedDetails.set(control, update);
      update();
    }

    function enhance(shadow) {
      const form = shadow.querySelector('form[data-testid="form"]');
      const success = shadow.querySelector('[data-testid="banner-success"]');
      if (success) {
        installStyle(shadow);
        root.classList.add('gml-form-designed');
      }
      if (!form) return;
      const mapped = fields.map(([key, selector]) => {
        const control = form.querySelector(selector);
        let container = control;
        while (container && container.parentElement !== form) container = container.parentElement;
        return { key, control, container };
      });
      if (mapped.some((field) => !field.container)) return;

      installStyle(shadow);
      form.classList.add('gml-designed-form');
      form.closest('[data-sizing="form-wrapper"]')?.classList.add('gml-designed-wrapper');
      mapped.forEach(({ key, control, container }) => {
        container.dataset.gmlField = key;
        if (key === 'details') enhanceDetails(control, container);
        if (key === 'phone') container.dataset.gmlPhoneLabel = template.dataset.phoneLabel;
        if (['brand', 'condition', 'included'].includes(key)) {
          container.style.setProperty('--gml-select-placeholder', JSON.stringify(template.dataset[key + 'Placeholder']));
        }
        if (['front', 'back', 'accessories'].includes(key)) {
          const content = container.querySelector('[class*="_formFileInputContent_"]');
          if (content && !content.querySelector('[data-gml-photo-caption]')) {
            const caption = document.createElement('span');
            caption.dataset.gmlPhotoCaption = '';
            caption.textContent = template.dataset[key + 'Label'];
            caption.setAttribute('aria-hidden', 'true');
            content.prepend(caption);
          }
        }
      });

      [['1', 'name'], ['2', 'brand'], ['3', 'front']].forEach(([number, first]) => {
        if (!form.querySelector('[data-gml-card-title="' + number + '"]')) {
          const heading = template.content.querySelector('[data-gml-card-title="' + number + '"]').cloneNode(true);
          form.insertBefore(heading, mapped.find((field) => field.key === first).container);
        }
        if (!form.querySelector('[data-gml-card-surface="' + number + '"]')) {
          const surface = document.createElement('div');
          surface.dataset.gmlCardSurface = number;
          surface.setAttribute('aria-hidden', 'true');
          form.append(surface);
        }
      });
      root.classList.add('gml-form-designed');
    }

    function installStyle(shadow) {
      if (!shadow.querySelector('[data-gml-design-style]')) {
        shadow.append(template.content.querySelector('[data-gml-design-style]').cloneNode(true));
      }
    }

    function connect() {
      if (stopped) return;
      app.querySelectorAll('shopify-forms-embed').forEach((host) => {
        const shadow = host.shadowRoot;
        if (!shadow) return;
        if (!watched.has(shadow)) {
          watched.add(shadow);
          const observer = new MutationObserver(schedule);
          observer.observe(shadow, { childList: true, subtree: true });
          observers.push(observer);
        }
        enhance(shadow);
      });
    }

    function schedule() {
      if (stopped || frame) return;
      frame = requestAnimationFrame(() => { frame = 0; connect(); });
    }
    const mount = new MutationObserver(schedule);
    mount.observe(app, { childList: true, subtree: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(app);
    window.customElements.whenDefined('shopify-forms-embed').then(schedule);
    connect();
    function unload(event) {
      if (!event.target.contains(root)) return;
      stopped = true;
      cancelAnimationFrame(frame);
      mount.disconnect();
      resize.disconnect();
      observers.forEach((observer) => observer.disconnect());
      document.removeEventListener('shopify:section:unload', unload);
    }
    document.addEventListener('shopify:section:unload', unload);
  }
  const initAll = (scope = document) => scope.querySelectorAll('[data-gml-sell]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

